import { c as _c } from "react/compiler-runtime";
import type { Entity, EntityOnboarding, GetEntitiesByFpIdMatchSignalsResponse, RiskSignal } from '@onefootprint/request-types/dashboard';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getOnboardingQueries, getTraceRuleCounts } from '@/components/entity-onboarding-details';
import groupRuleResults from '@/entities/components/details/utils/group-rule-results';
import { getEntityDuplicateDataOptions } from '@/entity/hooks/use-entity-duplicate-data';
import usePermissions from '@/hooks/use-permissions';
import type { SectionKey } from '../../../../onboardings.types';
import type { SectionCount } from '../../../onboarding-section';
type CountLabels = {
  withHigh: (total: number, count: number) => string;
  withMedium: (total: number, count: number) => string;
  triggered: (count: number) => string;
  needsReview: (count: number) => string;
};
const getRiskSignalCount = (riskSignals: RiskSignal[], labels: CountLabels): SectionCount | undefined => {
  if (!riskSignals.length) {
    return undefined;
  }
  const high = riskSignals.filter(({
    severity
  }) => severity === 'high').length;
  const medium = riskSignals.filter(({
    severity
  }) => severity === 'medium').length;
  if (high) {
    return {
      count: riskSignals.length,
      countTone: 'error',
      countDescription: labels.withHigh(riskSignals.length, high)
    };
  }
  if (medium) {
    return {
      count: riskSignals.length,
      countTone: 'warning',
      countDescription: labels.withMedium(riskSignals.length, medium)
    };
  }
  return {
    count: riskSignals.length
  };
};
const getValidationCount = (matchSignals: GetEntitiesByFpIdMatchSignalsResponse, labels: CountLabels): SectionCount | undefined => {
  const levels = Object.values(matchSignals).flatMap(validation => validation ? [validation.matchLevel] : []);
  // Every card that isn't a clean match needs a look; red only when a field outright failed.
  const needsReview = levels.filter(level => level !== 'exact').length;
  if (!needsReview) {
    return undefined;
  }
  return {
    count: needsReview,
    countTone: levels.includes('no_match') ? 'error' : 'warning',
    countDescription: labels.needsReview(needsReview)
  };
};

// A placeholder count belongs to the last onboarding, so only its size is kept: none of it is colored or read aloud.
const hold = (count: SectionCount | undefined, isPlaceholderData: boolean): SectionCount | undefined => count && isPlaceholderData ? {
  count: count.count,
  isCountPending: true
} : count;

/**
 * The count by each section's title, colored when it holds a problem, so the reader sees where to look. Shares its
 * queries with the sections themselves; empty sections get no count.
 */
const useSectionCounts = (entity, onboarding) => {
  const $ = _c(20);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "onboardings.tabs.counts"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t0);
  const isPerson = entity.kind === "person";
  const {
    isAuditor
  } = usePermissions();
  const queries = getOnboardingQueries(entity, onboarding);
  const riskSignals = useQuery({
    ...queries.riskSignals,
    placeholderData: keepPreviousData
  });
  const matchSignals = useQuery({
    ...queries.matchSignals,
    placeholderData: keepPreviousData
  });
  const trace = useQuery({
    ...queries.trace,
    placeholderData: keepPreviousData
  });
  const ruleSetResult = useQuery({
    ...queries.ruleSetResult,
    placeholderData: keepPreviousData
  });
  let t1;
  if ($[1] !== entity.id || $[2] !== onboarding.completedAt) {
    t1 = getEntityDuplicateDataOptions(entity.id, onboarding.completedAt);
    $[1] = entity.id;
    $[2] = onboarding.completedAt;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = isPerson && !isAuditor;
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      ...t1,
      enabled: t2,
      placeholderData: keepPreviousData
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const dupes = useQuery(t3);
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== t) {
    t4 = (total, count) => t(_temp, {
      total,
      count
    });
    t5 = (total_0, count_0) => t(_temp2, {
      total: total_0,
      count: count_0
    });
    t6 = count_1 => t(_temp3, {
      count: count_1
    });
    t7 = count_2 => count_2 === 1 ? t(_temp4, {
      count: 1
    }) : t(_temp5, {
      count: count_2
    });
    $[7] = t;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
  }
  const labels = {
    withHigh: t4,
    withMedium: t5,
    triggered: t6,
    needsReview: t7
  };
  const triggered = onboarding.hasGraphTrace ? trace.data && getTraceRuleCounts(trace.data).triggered : queries.ruleSetResult.enabled && ruleSetResult.data && Object.values(groupRuleResults(ruleSetResult.data.ruleResults).triggered).reduce(_temp6, 0);
  const isRulesPlaceholder = onboarding.hasGraphTrace ? trace.isPlaceholderData : ruleSetResult.isPlaceholderData;
  const t8 = hold(riskSignals.data && getRiskSignalCount(riskSignals.data, labels), riskSignals.isPlaceholderData);
  const t9 = hold(triggered ? {
    count: triggered,
    countTone: "warning",
    countDescription: labels.triggered(triggered)
  } : undefined, isRulesPlaceholder);
  const t10 = isPerson ? hold(matchSignals.data && getValidationCount(matchSignals.data, labels), matchSignals.isPlaceholderData) : undefined;
  let t11;
  if ($[12] !== dupes || $[13] !== isPerson) {
    t11 = isPerson && dupes.data?.data.length ? hold({
      count: dupes.data.data.length
    }, dupes.isPlaceholderData) : undefined;
    $[12] = dupes;
    $[13] = isPerson;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t8 || $[18] !== t9) {
    t12 = {
      "risk-signals": t8,
      rules: t9,
      "field-validations": t10,
      "duplicate-data": t11
    };
    $[15] = t10;
    $[16] = t11;
    $[17] = t8;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
};
export default useSectionCounts;
function _temp($) {
  return $.withHigh;
}
function _temp2($_0) {
  return $_0.withMedium;
}
function _temp3($_1) {
  return $_1.triggered;
}
function _temp4($_2) {
  return $_2.needsReview_one;
}
function _temp5($_3) {
  return $_3.needsReview_other;
}
function _temp6(count_3, rules) {
  return count_3 + (rules?.length ?? 0);
}